import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import PePapersDisclaimerGate from './shared/PePapersDisclaimerGate';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';
import {
    calculateFortbildungsRueckzahlung,
    convertKursDauerToMonate,
    formatEuro,
    formatKursDauerText,
    KURS_DAUER_EINHEIT_OPTIONS,
    type KursDauerEinheit,
} from '../../utils/pe-papers/fortbildungCalc';

const FB_CONTRACT_PDF_CSS = `
  .av-a4-page {
    display: block !important;
    width: 100% !important;
    max-width: 210mm !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    color: #0f172a !important;
    font-size: 12px !important;
    line-height: 1.48 !important;
    padding: 11mm 13mm !important;
    background: #ffffff !important;
  }
  .av-a4-page * { box-sizing: border-box !important; }
  .av-a4-page .pdf-letterhead {
    width: 100% !important;
    text-align: center !important;
    border-bottom: 2px solid #1e293b !important;
    margin: 0 0 12px !important;
    padding: 0 0 10px !important;
  }
  .av-a4-page .pdf-letterhead h1 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 17px !important;
    font-weight: 900 !important;
    letter-spacing: 0.08em !important;
    text-transform: uppercase !important;
    color: #0f172a !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 0 3px !important;
    text-align: center !important;
  }
  .av-a4-page .pdf-letterhead h2 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    color: #b48a56 !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    text-align: center !important;
  }
  .av-meta-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin: 0 0 12px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
  }
  .av-meta-col {
    display: table-cell !important;
    width: 50% !important;
    vertical-align: top !important;
    padding: 10px 12px !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
    text-align: left !important;
  }
  .av-meta-col + .av-meta-col { border-left: 1px solid #e2e8f0 !important; }
  .av-meta-label {
    display: block !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.12em !important;
    color: #64748b !important;
    margin: 0 0 4px !important;
  }
  .av-meta-col strong { color: #0f172a !important; font-weight: 700 !important; }
  .av-meta-col p {
    width: 100% !important;
    margin: 0 !important;
    text-align: left !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
  }
  .av-meta-col .av-meta-date {
    display: block !important;
    margin-top: 4px !important;
    color: #64748b !important;
    font-size: 11px !important;
  }
  .av-doc-section {
    display: block !important;
    width: 100% !important;
    margin: 0 0 12px !important;
  }
  .av-a4-page h3,
  .av-doc-section > h3 {
    display: block !important;
    width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    text-align: left !important;
    background: #1e293b !important;
    color: #f8fafc !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 6px 10px !important;
    margin: 0 0 8px !important;
  }
  .av-a4-page p,
  .av-doc-p,
  .av-doc-section p {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: 12px !important;
    line-height: 1.48 !important;
    color: #1e293b !important;
    text-align: justify !important;
    margin: 0 0 8px !important;
  }
  .av-table-wrap {
    display: block !important;
    width: 100% !important;
    margin: 0 0 10px !important;
  }
  .av-table-wrap table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    font-size: 12.5px !important;
  }
  .av-table-wrap thead th,
  .av-table-wrap th {
    background-color: #f1f5f9 !important;
    color: #0f172a !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    padding: 4px 6px !important;
    border: 1px solid #e2e8f0 !important;
    border-bottom: 1px solid #cbd5e1 !important;
    text-align: left !important;
  }
  .av-table-wrap td {
    padding: 4px 6px !important;
    border: 1px solid #e2e8f0 !important;
    color: #334155 !important;
    text-align: left !important;
    vertical-align: middle !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    line-height: 1.3 !important;
  }
  .ab-sig-table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    margin-top: 24px !important;
    border: none !important;
  }
  .ab-sig-table td {
    width: 50% !important;
    vertical-align: top !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
  }
  .ab-sig-table p {
    text-align: left !important;
    margin: 0 !important;
    width: auto !important;
    max-width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
  }
  @media print {
    @page { size: A4; margin: 0; }
    body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .av-a4-page {
      box-shadow: none !important;
      margin: 0 !important;
      max-width: none !important;
      width: 100% !important;
      min-height: 0 !important;
    }
  }
`;

const FB_PRINT_CSS = `
${FB_CONTRACT_PDF_CSS}
html, body {
  margin: 0 !important;
  padding: 0 !important;
  background: #fff !important;
}
`;

interface FortbildungModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

interface BetriebFormData {
    firmenname: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface MitarbeiterFormData {
    name: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface BetriebFieldLocks {
    firmenname: boolean;
    strasse: boolean;
    plz: boolean;
    ort: boolean;
}

type TeamPool = 'produktiv' | 'unproduktiv' | 'azubi';

type TeamEmployeeOption = {
    id: string;
    name: string;
    pool: TeamPool;
    adresse: string;
};

const INITIAL_MITARBEITER_DATA: MitarbeiterFormData = {
    name: '',
    strasse: '',
    plz: '',
    ort: '',
};

const A4_PREVIEW_PAGE_CLASS =
    'av-a4-page bg-white text-slate-900 my-4 mx-auto w-full max-w-[210mm] h-auto min-h-0 shadow-xl rounded-sm text-left block overflow-visible print:my-0 print:shadow-none';

const DOC_P_CLASS = 'av-doc-p mb-2 text-justify text-slate-800 leading-relaxed';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const DATE_INPUT_CLASS = `${INPUT_CLASS} cursor-pointer kombi-date-input`;
const DURATION_NUMBER_INPUT_CLASS =
    'w-1/3 min-w-[100px] shrink-0 rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';
const DURATION_SELECT_CLASS =
    'flex-1 min-w-0 rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';
const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';
const READONLY_INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-accent-text dark:text-[#E6C299] px-4 py-2.5 text-sm cursor-not-allowed opacity-90';
const ERROR_INPUT_RING = 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/20';

const BESTANDSSCHUTZ_KLAUSEL =
    'Alle übrigen Vereinbarungen des Hauptarbeitsvertrages bleiben unberührt und behalten vollumfänglich ihre Gültigkeit.';

const isEmpty = (value: string) => !String(value || '').trim();

/** Legacy-Archiv / Stammdaten: einzeilige `adresse` → Straße / PLZ / Ort */
const parseLegacyMitarbeiterAdresse = (
    adresse: string
): Pick<MitarbeiterFormData, 'strasse' | 'plz' | 'ort'> => {
    const raw = String(adresse || '').trim();
    if (!raw) return { strasse: '', plz: '', ort: '' };

    const lines = raw
        .split(/[\n;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

    const plzOrtMatch = (value: string) => {
        const m = value.match(/^(\d{4,5})\s+(.+)$/);
        return m ? { plz: m[1], ort: m[2].trim() } : null;
    };

    if (lines.length === 1) {
        const parts = lines[0]
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (parts.length >= 2) {
            const last = plzOrtMatch(parts[parts.length - 1]);
            if (last) {
                return {
                    strasse: parts.slice(0, -1).join(', '),
                    plz: last.plz,
                    ort: last.ort,
                };
            }
        }
        const single = plzOrtMatch(lines[0]);
        if (single) return { strasse: '', ...single };
        return { strasse: lines[0], plz: '', ort: '' };
    }

    const lastLine = plzOrtMatch(lines[lines.length - 1]);
    if (lastLine) {
        return {
            strasse: lines.slice(0, -1).join(', '),
            plz: lastLine.plz,
            ort: lastLine.ort,
        };
    }
    return { strasse: lines[0], plz: '', ort: lines.slice(1).join(' ') };
};

const normalizeMitarbeiterData = (incoming: any): MitarbeiterFormData => {
    const src = incoming && typeof incoming === 'object' ? incoming : {};
    const hasStructured =
        !isEmpty(String(src.strasse || '')) ||
        !isEmpty(String(src.plz || '')) ||
        !isEmpty(String(src.ort || ''));
    if (!hasStructured && typeof src.adresse === 'string' && src.adresse.trim()) {
        const parsed = parseLegacyMitarbeiterAdresse(src.adresse);
        return {
            name: String(src.name || ''),
            strasse: parsed.strasse,
            plz: parsed.plz,
            ort: parsed.ort,
        };
    }
    return {
        name: String(src.name || ''),
        strasse: String(src.strasse || ''),
        plz: String(src.plz || ''),
        ort: String(src.ort || ''),
    };
};

const formatMitarbeiterAdresseInline = (data: MitarbeiterFormData): string => {
    const line1 = data.strasse.trim();
    const line2 = [data.plz.trim(), data.ort.trim()].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join(', ');
};

const formatTodayGerman = (): string => {
    const now = new Date();
    return `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
};

const formatDisplayDate = (value: string): string => {
    if (!value.trim()) return '____________';
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
    return value.trim();
};

const buildBetriebFromClient = (clientData: any): { betrieb: BetriebFormData; locks: BetriebFieldLocks } => {
    const firmenname = clientData?.salonname || clientData?.firmenname || '';
    const strasse = clientData?.strasse || '';
    const plz = clientData?.plz || '';
    const ort = clientData?.ort || '';
    return {
        betrieb: { firmenname, strasse, plz, ort },
        locks: {
            firmenname: !isEmpty(firmenname),
            strasse: !isEmpty(strasse),
            plz: !isEmpty(plz),
            ort: !isEmpty(ort),
        },
    };
};

const FortbildungModal: React.FC<FortbildungModalProps> = ({ isOpen, onClose, initialData, onArchived }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [hauptvertragDatum, setHauptvertragDatum] = useState('');
    const [seminarkostenEur, setSeminarkostenEur] = useState('');
    const [fortbildungTitel, setFortbildungTitel] = useState('');
    const [kursDauerAnzahl, setKursDauerAnzahl] = useState('');
    const [kursDauerEinheit, setKursDauerEinheit] = useState<KursDauerEinheit>('Monat(e)');
    const [abschlussDatum, setAbschlussDatum] = useState('');
    const [betriebData, setBetriebData] = useState<BetriebFormData>({ firmenname: '', strasse: '', plz: '', ort: '' });
    const [betriebLocks, setBetriebLocks] = useState<BetriebFieldLocks>({
        firmenname: false,
        strasse: false,
        plz: false,
        ort: false,
    });
    const [mitarbeiterData, setMitarbeiterData] = useState<MitarbeiterFormData>(INITIAL_MITARBEITER_DATA);
    const [selectedTeamEmployeeId, setSelectedTeamEmployeeId] = useState('');
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);

    const printContainerRef = useRef<HTMLDivElement>(null);
    const clientData = useAppStore((state: any) => state.clientData);
    const { personalData, unproductivePersonalData, azubiAssistenzData } = usePersonalDataSelector();

    const teamEmployees = useMemo((): TeamEmployeeOption[] => {
        const mapPool = (list: any[], pool: TeamPool): TeamEmployeeOption[] =>
            (Array.isArray(list) ? list : [])
                .filter((emp) => emp?.id)
                .map((emp) => ({
                    id: String(emp.id),
                    name: String(emp.name || '').trim() || 'Ohne Name',
                    pool,
                    adresse: String(emp.adresse || emp.info || '').trim(),
                }));
        return [
            ...mapPool(personalData, 'produktiv'),
            ...mapPool(unproductivePersonalData, 'unproduktiv'),
            ...mapPool(azubiAssistenzData, 'azubi'),
        ];
    }, [personalData, unproductivePersonalData, azubiAssistenzData]);

    const kursDauerInMonaten = useMemo(() => {
        const anzahl = Number(kursDauerAnzahl);
        return convertKursDauerToMonate(anzahl, kursDauerEinheit);
    }, [kursDauerAnzahl, kursDauerEinheit]);

    const fortbildungsBerechnung = useMemo(() => {
        const kosten = Number(seminarkostenEur);
        if (!Number.isFinite(kosten) || !Number.isFinite(kursDauerInMonaten) || kursDauerInMonaten <= 0) return null;
        return calculateFortbildungsRueckzahlung(kosten, kursDauerInMonaten);
    }, [seminarkostenEur, kursDauerInMonaten]);

    const resetForm = () => {
        setCurrentStep(1);
        setHauptvertragDatum('');
        setSeminarkostenEur('');
        setFortbildungTitel('');
        setKursDauerAnzahl('');
        setKursDauerEinheit('Monat(e)');
        setAbschlussDatum('');
        setMitarbeiterData(INITIAL_MITARBEITER_DATA);
        setSelectedTeamEmployeeId('');
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(null);
    };

    useEffect(() => {
        if (!isOpen) {
            resetForm();
            return;
        }
        const { betrieb, locks } = buildBetriebFromClient(clientData);
        resetForm();
        setBetriebData(betrieb);
        setBetriebLocks(locks);
        setArchiveDocId(initialData?.id || null);

        if (initialData) {
            if (typeof initialData.hauptvertragDatum === 'string') setHauptvertragDatum(initialData.hauptvertragDatum);
            if (typeof initialData.fortbildungTitel === 'string') setFortbildungTitel(initialData.fortbildungTitel);
            if (initialData.seminarkostenEur != null && initialData.seminarkostenEur !== '') {
                setSeminarkostenEur(String(initialData.seminarkostenEur));
            }
            if (initialData.kursDauerAnzahl != null && initialData.kursDauerAnzahl !== '') {
                setKursDauerAnzahl(String(initialData.kursDauerAnzahl));
            }
            if (
                typeof initialData.kursDauerEinheit === 'string' &&
                KURS_DAUER_EINHEIT_OPTIONS.includes(initialData.kursDauerEinheit as KursDauerEinheit)
            ) {
                setKursDauerEinheit(initialData.kursDauerEinheit as KursDauerEinheit);
            }
            if (typeof initialData.abschlussDatum === 'string') setAbschlussDatum(initialData.abschlussDatum);
            if (initialData.betriebData && typeof initialData.betriebData === 'object') {
                setBetriebData({ ...betrieb, ...initialData.betriebData });
                setBetriebLocks({
                    firmenname: !isEmpty(String(initialData.betriebData.firmenname ?? betrieb.firmenname)),
                    strasse: !isEmpty(String(initialData.betriebData.strasse ?? betrieb.strasse)),
                    plz: !isEmpty(String(initialData.betriebData.plz ?? betrieb.plz)),
                    ort: !isEmpty(String(initialData.betriebData.ort ?? betrieb.ort)),
                });
            }
            if (initialData.mitarbeiterData && typeof initialData.mitarbeiterData === 'object') {
                setMitarbeiterData(normalizeMitarbeiterData(initialData.mitarbeiterData));
            }
            if (typeof initialData.selectedTeamEmployeeId === 'string') {
                setSelectedTeamEmployeeId(initialData.selectedTeamEmployeeId);
            }
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) {
                setCurrentStep(step);
            }
            if (initialData.confirmedDisclaimer) {
                setDisclaimerAccepted(true);
            }
        }
    }, [isOpen, clientData, initialData]);

    const validateStep1 = (): string[] => {
        const errors: string[] = [];
        if (isEmpty(betriebData.firmenname)) errors.push('betrieb_firmenname');
        if (isEmpty(betriebData.strasse)) errors.push('betrieb_strasse');
        if (isEmpty(betriebData.plz)) errors.push('betrieb_plz');
        if (isEmpty(betriebData.ort)) errors.push('betrieb_ort');
        if (isEmpty(mitarbeiterData.name)) errors.push('mitarbeiter_name');
        if (isEmpty(mitarbeiterData.strasse)) errors.push('mitarbeiter_strasse');
        if (isEmpty(mitarbeiterData.plz)) errors.push('mitarbeiter_plz');
        if (isEmpty(mitarbeiterData.ort)) errors.push('mitarbeiter_ort');
        if (isEmpty(hauptvertragDatum)) errors.push('hauptvertrag_datum');
        if (isEmpty(fortbildungTitel)) errors.push('fortbildung_titel');
        const kosten = Number(seminarkostenEur);
        const dauerAnzahl = Number(kursDauerAnzahl);
        if (!seminarkostenEur.trim() || !Number.isFinite(kosten) || kosten <= 0) errors.push('seminarkosten_eur');
        if (!kursDauerAnzahl.trim() || !Number.isFinite(dauerAnzahl) || dauerAnzahl <= 0) errors.push('kurs_dauer_anzahl');
        if (isEmpty(abschlussDatum)) errors.push('abschluss_datum');
        if (!fortbildungsBerechnung) errors.push('seminarkosten_eur');
        return errors;
    };

    const isFieldInvalid = (field: string) => invalidFields.has(field);
    const getInputClass = (baseClass: string, field: string) =>
        isFieldInvalid(field) ? `${baseClass} ${ERROR_INPUT_RING}` : baseClass;

    const clearFieldError = (field: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const updateBetriebField = <K extends keyof BetriebFormData>(key: K, value: BetriebFormData[K]) => {
        setBetriebData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`betrieb_${key}`);
    };

    const updateMitarbeiterField = <K extends keyof MitarbeiterFormData>(key: K, value: MitarbeiterFormData[K]) => {
        setMitarbeiterData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`mitarbeiter_${key}`);
    };

    const handleTeamEmployeeSelect = (employeeId: string) => {
        setSelectedTeamEmployeeId(employeeId);
        if (!employeeId) return;
        const emp = teamEmployees.find((e) => e.id === employeeId);
        if (!emp) return;
        const parsedAdresse = parseLegacyMitarbeiterAdresse(emp.adresse);
        setMitarbeiterData({
            name: emp.name === 'Ohne Name' ? '' : emp.name,
            strasse: parsedAdresse.strasse,
            plz: parsedAdresse.plz,
            ort: parsedAdresse.ort,
        });
        clearFieldError('mitarbeiter_name');
        clearFieldError('mitarbeiter_strasse');
        clearFieldError('mitarbeiter_plz');
        clearFieldError('mitarbeiter_ort');
    };

    const renderFieldError = (field: string) =>
        isFieldInvalid(field) ? <p className="text-[10px] text-red-400 mt-1">Dieses Feld ist erforderlich</p> : null;

    const renderBetriebField = (field: keyof BetriebFormData, label: string) => {
        const lockKey = field as keyof BetriebFieldLocks;
        const fieldId = `betrieb_${field}`;
        const isLocked = betriebLocks[lockKey];
        return (
            <div>
                <label className={LABEL_CLASS}>{label}</label>
                <input
                    type="text"
                    name={field}
                    autoComplete={
                        field === 'strasse'
                            ? 'street-address'
                            : field === 'plz'
                              ? 'postal-code'
                              : field === 'ort'
                                ? 'address-level2'
                                : field === 'firmenname'
                                  ? 'organization'
                                  : 'on'
                    }
                    value={betriebData[field]}
                    readOnly={isLocked}
                    onChange={(e) => !isLocked && updateBetriebField(field, e.target.value)}
                    className={getInputClass(isLocked ? READONLY_INPUT_CLASS : INPUT_CLASS, fieldId)}
                />
                {renderFieldError(fieldId)}
            </div>
        );
    };

    const renderDateField = (label: string, value: string, onChange: (v: string) => void, fieldId: string) => (
        <div>
            <label className={LABEL_CLASS}>{label}</label>
            <input
                type="date"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={getInputClass(DATE_INPUT_CLASS, fieldId)}
            />
            {renderFieldError(fieldId)}
        </div>
    );

    const renderDocument = () => {
        if (!fortbildungsBerechnung) return null;
        const kosten = Number(seminarkostenEur);
        const mitarbeiterName = mitarbeiterData.name.trim() || '____________';
        const titelDerFortbildung = fortbildungTitel.trim() || '____________';
        const ortDatum = `${betriebData.ort || '____________'}, ${formatTodayGerman()}`;

        return (
            <div ref={printContainerRef} id="pepapers-fortbildung-print" className="w-full h-auto overflow-visible">
                <style>{FB_CONTRACT_PDF_CSS}</style>
                <div className={`${A4_PREVIEW_PAGE_CLASS} min-h-[297mm]`}>
                    <div className="pdf-letterhead text-center">
                        <h1>Fortbildungsrückzahlung</h1>
                        <h2>PreisExpert · PEPapers</h2>
                    </div>

                    <div className="av-meta-grid">
                        <div className="av-meta-col">
                            <p className="av-meta-label">Arbeitgeber</p>
                            <p>
                                <strong>{betriebData.firmenname || '____________'}</strong>
                                {betriebData.strasse ? (
                                    <>
                                        <br />
                                        {betriebData.strasse}
                                    </>
                                ) : null}
                                {betriebData.plz || betriebData.ort ? (
                                    <>
                                        <br />
                                        {[betriebData.plz, betriebData.ort].filter(Boolean).join(' ')}
                                    </>
                                ) : null}
                                <span className="av-meta-date">Datum: {formatTodayGerman()}</span>
                            </p>
                        </div>
                        <div className="av-meta-col">
                            <p className="av-meta-label">Arbeitnehmer/in</p>
                            <p>
                                <strong>{mitarbeiterName}</strong>
                                {mitarbeiterData.strasse ? (
                                    <>
                                        <br />
                                        {mitarbeiterData.strasse}
                                    </>
                                ) : null}
                                {mitarbeiterData.plz || mitarbeiterData.ort ? (
                                    <>
                                        <br />
                                        {[mitarbeiterData.plz, mitarbeiterData.ort].filter(Boolean).join(' ')}
                                    </>
                                ) : !mitarbeiterData.strasse ? (
                                    <>
                                        <br />
                                        ____________
                                    </>
                                ) : null}
                            </p>
                        </div>
                    </div>

                    <div className="av-doc-section">
                        <h3>Vereinbarung</h3>
                        <p className={DOC_P_CLASS}>
                            Sehr geehrte/r Frau/Herr <strong>{mitarbeiterName}</strong>,
                        </p>
                        <p className={DOC_P_CLASS}>
                            der Arbeitgeber hat für Sie in eine betriebliche Fortbildung / ein Seminar mit dem Titel{' '}
                            <strong>{`„${titelDerFortbildung}"`}</strong> mit Gesamtkosten in Höhe von{' '}
                            <strong>{formatEuro(kosten)} EUR netto</strong> investiert. Die Kursdauer betrug{' '}
                            <strong>{formatKursDauerText(kursDauerAnzahl, kursDauerEinheit)}</strong>. Der erfolgreiche
                            Abschluss erfolgte am <strong>{formatDisplayDate(abschlussDatum)}</strong>. Ab diesem Datum
                            beginnt die vereinbarte Bindungsfrist.
                        </p>
                        <p className={DOC_P_CLASS}>
                            Die Parteien vereinbaren eine Bindungsfrist von{' '}
                            <strong>{fortbildungsBerechnung.bindungsmonate} Monaten</strong>, orientiert an der
                            Rechtsprechung des Bundesarbeitsgerichts zur zulässigen Dauer betrieblicher Bindungsfristen
                            bei Fortbildungsmaßnahmen.
                        </p>
                        <p className={DOC_P_CLASS}>
                            Ein Rückzahlungsanspruch besteht ausschließlich, wenn das Arbeitsverhältnis vor Ablauf der
                            Bindungsfrist aufgrund <strong>eigenkündigender Beendigung durch den Arbeitnehmer</strong>{' '}
                            oder aufgrund einer <strong>verhaltensbedingten Kündigung durch den Arbeitgeber</strong>{' '}
                            endet.
                        </p>
                    </div>

                    <div className="av-doc-section">
                        <h3>Abschmelzungstabelle</h3>
                        <p className={DOC_P_CLASS}>
                            Monatliche lineare Abschmelzung der Rückzahlungssumme (
                            {formatEuro(fortbildungsBerechnung.monatlicheMinderung)} EUR netto pro Monat):
                        </p>
                        <div className="av-table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Bindungsmonat</th>
                                        <th>Verbleibende Rückzahlung (EUR netto)</th>
                                        <th>Monatliche Minderung (EUR netto)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fortbildungsBerechnung.staffel.map((entry) => (
                                        <tr key={entry.monat}>
                                            <td>
                                                {entry.monat === 0
                                                    ? 'Abschluss (Beginn)'
                                                    : `Nach Monat ${entry.monat}`}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <strong>{formatEuro(entry.verbleibendeSumme)} EUR netto</strong>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {entry.monat === 0
                                                    ? '\u2014'
                                                    : `${formatEuro(fortbildungsBerechnung.monatlicheMinderung)} EUR netto`}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="av-doc-section">
                        <h3>Bestandsschutz</h3>
                        <p className={DOC_P_CLASS}>
                            {BESTANDSSCHUTZ_KLAUSEL.replace(
                                'des Hauptarbeitsvertrages',
                                `des Hauptarbeitsvertrages vom ${formatDisplayDate(hauptvertragDatum)}`
                            )}
                        </p>
                    </div>

                    <table
                        className="ab-sig-table"
                        style={{
                            width: '100%',
                            marginTop: '24px',
                            borderCollapse: 'collapse',
                            tableLayout: 'fixed',
                        }}
                    >
                        <tbody>
                            <tr>
                                <td style={{ width: '50%', paddingRight: '20px', verticalAlign: 'top' }}>
                                    <p style={{ margin: 0, fontSize: '11px', color: '#475569' }}>
                                        Ort, Datum: {ortDatum}
                                    </p>
                                    <div style={{ height: '36px' }} aria-hidden="true" />
                                    <div
                                        style={{
                                            height: '1px',
                                            backgroundColor: '#1e293b',
                                            width: '200px',
                                            maxWidth: '100%',
                                            marginBottom: '6px',
                                        }}
                                        aria-hidden="true"
                                    />
                                    <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
                                        Unterschrift Arbeitgeber/in
                                    </p>
                                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                                        {betriebData.firmenname || 'Arbeitgeber'}
                                    </p>
                                </td>
                                <td style={{ width: '50%', paddingLeft: '20px', verticalAlign: 'top' }}>
                                    <p style={{ margin: 0, fontSize: '11px', color: '#475569' }}>
                                        Ort, Datum: ________________________
                                    </p>
                                    <div style={{ height: '36px' }} aria-hidden="true" />
                                    <div
                                        style={{
                                            height: '1px',
                                            backgroundColor: '#1e293b',
                                            width: '200px',
                                            maxWidth: '100%',
                                            marginBottom: '6px',
                                        }}
                                        aria-hidden="true"
                                    />
                                    <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
                                        Unterschrift Arbeitnehmer/in
                                    </p>
                                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                                        {mitarbeiterData.name || 'Arbeitnehmer/in'}
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const handlePrintIsolated = () => {
        if (!disclaimerAccepted) return;
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:absolute;width:0;height:0;border:none';
        document.body.appendChild(iframe);
        const printWindow = iframe.contentWindow;
        if (!printWindow) {
            document.body.removeChild(iframe);
            return;
        }
        const printContent = printContainerRef.current?.innerHTML || '';
        const doc = printWindow.document;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><title>Fortbildungsrückzahlung</title><style>${FB_PRINT_CSS}</style></head><body>${printContent}</body></html>`);
        doc.close();
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setTimeout(() => iframe.parentNode && document.body.removeChild(iframe), 1000);
        }, 500);
    };

    const handleDownloadPdf = async () => {
        if (!disclaimerAccepted || isPdfDownloading) return;
        const html = printContainerRef.current?.innerHTML || '';
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `Fortbildungsrueckzahlung_${dateIso}.pdf`,
                scrubMode: 'minimal',
                splitPrintBreaks: true,
                capturePadPx: 0,
                letterheadOnBreaks: false,
                extraCss: FB_CONTRACT_PDF_CSS,
            });
        } catch (err: any) {
            console.error('PEPapers Fortbildung PDF-Download fehlgeschlagen:', err);
            window.alert(
                'PDF konnte nicht heruntergeladen werden: ' +
                    (String(err?.message || '').match(/setEnd|Range|offset/i)
                        ? 'PDF-Erstellung ist fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
                        : err?.message || 'Unbekannter Fehler')
            );
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && !disclaimerAccepted) return;
        setIsSaving(true);
        setArchiveFeedback('idle');
        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'fortbildungsrueckzahlung',
                status: asDraft ? 'entwurf' : 'final',
                salonName: betriebData.firmenname || 'Unbekannter Salon',
                mitarbeiterName: mitarbeiterData.name.trim(),
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    hauptvertragDatum,
                    fortbildungTitel,
                    seminarkostenEur: Number(seminarkostenEur) || 0,
                    kursDauerAnzahl: Number(kursDauerAnzahl) || 0,
                    kursDauerEinheit,
                    abschlussDatum,
                    fortbildungsBerechnung,
                    betriebData,
                    mitarbeiterData: {
                        ...mitarbeiterData,
                        adresse: formatMitarbeiterAdresseInline(mitarbeiterData),
                    },
                    selectedTeamEmployeeId: selectedTeamEmployeeId || null,
                    generatedHtml: printContainerRef.current?.innerHTML || '',
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => {
                    onClose();
                }, 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers Fortbildung Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{`input.kombi-date-input[type='date']::-webkit-calendar-picker-indicator{filter:invert(1);cursor:pointer}`}</style>
            <div className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md" onClick={onClose} />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${currentStep === 2 ? 'max-w-4xl' : 'max-w-lg'} max-h-[calc(100vh-9.5rem)] flex min-h-0 flex-col overflow-hidden relative`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button type="button" onClick={onClose} className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] rounded-lg p-2 border border-pe-border-strong dark:border-[#333] z-[60]">
                        <X className="w-4 h-4" />
                    </button>
                    <div className="bg-pe-elevated dark:bg-[#111622] shrink-0 border-b border-pe-border dark:border-[#222] px-6 pt-10 pb-4">
                        <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8">
                            Fortbildungsrückzahlung
                        </h3>
                        <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                            Schritt {currentStep} von 2
                        </p>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar">
                        {currentStep === 1 ? (
                            <div className="space-y-6">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">Betriebsdaten</p>
                                    <div className="space-y-4">
                                        {renderBetriebField('firmenname', 'Firma / Salonname')}
                                        {renderBetriebField('strasse', 'Straße & Hausnummer')}
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            {renderBetriebField('plz', 'PLZ')}
                                            <div className="sm:col-span-2">{renderBetriebField('ort', 'Ort')}</div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">Mitarbeiter-Daten</p>
                                    <div className="space-y-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Aus Team wählen (optional)</label>
                                            <select
                                                value={selectedTeamEmployeeId}
                                                onChange={(e) => handleTeamEmployeeSelect(e.target.value)}
                                                className={INPUT_CLASS}
                                            >
                                                <option value="">— Manuell eintragen / Ex-Mitarbeiter —</option>
                                                {teamEmployees.map((emp) => (
                                                    <option key={`${emp.pool}-${emp.id}`} value={emp.id}>
                                                        {emp.name}
                                                        {emp.pool === 'azubi'
                                                            ? ' (Azubi/Assistenz)'
                                                            : emp.pool === 'unproduktiv'
                                                              ? ' (unproduktiv)'
                                                              : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {teamEmployees.length === 0 ? (
                                                <p className="mt-1 text-[10px] text-pe-muted dark:text-slate-500">
                                                    Keine Personaldaten hinterlegt – Name und Adresse manuell
                                                    eintragen.
                                                </p>
                                            ) : (
                                                <p className="mt-1 text-[10px] text-pe-muted dark:text-slate-500">
                                                    Auswahl füllt Name und Adresse vor; für Ex-Mitarbeiter einfach
                                                    manuell lassen. Felder bleiben danach editierbar.
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Name des Mitarbeiters</label>
                                            <input
                                                type="text"
                                                name="name"
                                                autoComplete="name"
                                                value={mitarbeiterData.name}
                                                onChange={(e) => updateMitarbeiterField('name', e.target.value)}
                                                className={getInputClass(INPUT_CLASS, 'mitarbeiter_name')}
                                            />
                                            {renderFieldError('mitarbeiter_name')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Straße &amp; Hausnummer</label>
                                            <input
                                                type="text"
                                                name="strasse"
                                                autoComplete="street-address"
                                                value={mitarbeiterData.strasse}
                                                onChange={(e) => updateMitarbeiterField('strasse', e.target.value)}
                                                placeholder="z. B. Musterstraße 12"
                                                className={getInputClass(INPUT_CLASS, 'mitarbeiter_strasse')}
                                            />
                                            {renderFieldError('mitarbeiter_strasse')}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div>
                                                <label className={LABEL_CLASS}>PLZ</label>
                                                <input
                                                    type="text"
                                                    name="plz"
                                                    autoComplete="postal-code"
                                                    inputMode="numeric"
                                                    value={mitarbeiterData.plz}
                                                    onChange={(e) => updateMitarbeiterField('plz', e.target.value)}
                                                    placeholder="12345"
                                                    className={getInputClass(INPUT_CLASS, 'mitarbeiter_plz')}
                                                />
                                                {renderFieldError('mitarbeiter_plz')}
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label className={LABEL_CLASS}>Ort</label>
                                                <input
                                                    type="text"
                                                    name="ort"
                                                    autoComplete="address-level2"
                                                    value={mitarbeiterData.ort}
                                                    onChange={(e) => updateMitarbeiterField('ort', e.target.value)}
                                                    placeholder="Musterstadt"
                                                    className={getInputClass(INPUT_CLASS, 'mitarbeiter_ort')}
                                                />
                                                {renderFieldError('mitarbeiter_ort')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">Vertragsbezug & Fortbildung</p>
                                    <div className="space-y-4">
                                        {renderDateField('Datum des ursprünglichen Hauptarbeitsvertrags', hauptvertragDatum, (v) => { setHauptvertragDatum(v); clearFieldError('hauptvertrag_datum'); }, 'hauptvertrag_datum')}
                                        <div>
                                            <label className={LABEL_CLASS}>Name / Titel der Fortbildung</label>
                                            <input type="text" value={fortbildungTitel} onChange={(e) => { setFortbildungTitel(e.target.value); clearFieldError('fortbildung_titel'); }} className={getInputClass(INPUT_CLASS, 'fortbildung_titel')} />
                                            {renderFieldError('fortbildung_titel')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Gesamtkosten der Fortbildung (in € netto)</label>
                                            <input type="number" min={0.01} step={0.01} value={seminarkostenEur} onChange={(e) => { setSeminarkostenEur(e.target.value); clearFieldError('seminarkosten_eur'); }} className={getInputClass(INPUT_CLASS, 'seminarkosten_eur')} />
                                            {renderFieldError('seminarkosten_eur')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Dauer des Kurses / Seminars</label>
                                            <div className="flex gap-2 w-full mt-1">
                                                <input type="number" min={1} step={1} value={kursDauerAnzahl} onChange={(e) => { setKursDauerAnzahl(e.target.value); clearFieldError('kurs_dauer_anzahl'); }} className={getInputClass(DURATION_NUMBER_INPUT_CLASS, 'kurs_dauer_anzahl')} />
                                                <select value={kursDauerEinheit} onChange={(e) => setKursDauerEinheit(e.target.value as KursDauerEinheit)} className={getInputClass(DURATION_SELECT_CLASS, 'kurs_dauer_einheit')}>
                                                    {KURS_DAUER_EINHEIT_OPTIONS.map((e) => <option key={e} value={e}>{e}</option>)}
                                                </select>
                                            </div>
                                            {renderFieldError('kurs_dauer_anzahl')}
                                        </div>
                                        {fortbildungsBerechnung && (
                                            <div>
                                                <label className={LABEL_CLASS}>Zulässige Bindungsdauer (automatisch)</label>
                                                <input type="text" readOnly value={`${fortbildungsBerechnung.bindungsmonate} Monate`} className={READONLY_INPUT_CLASS} />
                                            </div>
                                        )}
                                        {renderDateField('Datum des erfolgreichen Abschlusses (Beginn Bindungsfrist)', abschlussDatum, (v) => { setAbschlussDatum(v); clearFieldError('abschluss_datum'); }, 'abschluss_datum')}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            renderDocument()
                        )}
                    </div>
                    {currentStep === 2 && (
                        <PePapersDisclaimerGate
                            accepted={disclaimerAccepted}
                            onAcceptedChange={setDisclaimerAccepted}
                        />
                    )}
                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0">
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button type="button" onClick={() => { setInvalidFields(new Set()); setCurrentStep(1); }} className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase border border-pe-border-strong text-pe-muted">
                                    Zurück
                                </button>
                            )}
                            {currentStep === 1 ? (
                                <>
                                    <button type="button" onClick={() => void handleArchive(true)} disabled={isSaving} className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase border border-pe-border-strong text-pe-muted disabled:opacity-50">
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                    </button>
                                    <button type="button" onClick={() => { const errors = validateStep1(); if (errors.length) { setInvalidFields(new Set(errors)); return; } setInvalidFields(new Set()); setCurrentStep(2); }} className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase bg-[#E6C299] text-black">
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button type="button" onClick={() => void handleArchive(true)} disabled={isSaving} className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase border border-pe-border-strong text-pe-muted disabled:opacity-50">
                                        {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                    </button>
                                    <button type="button" onClick={() => void handleArchive(false)} disabled={!disclaimerAccepted || isSaving} className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase border border-[#E6C299]/40 text-pe-accent-text disabled:opacity-50">
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : archiveFeedback === 'success' ? 'Gespeichert! ✓' : 'Im Archiv speichern'}
                                    </button>
                                    <button type="button" onClick={() => void handleDownloadPdf()} disabled={!disclaimerAccepted || isPdfDownloading} className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase bg-[#E6C299] text-black disabled:opacity-50">
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button type="button" onClick={handlePrintIsolated} disabled={!disclaimerAccepted} className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase border border-[#E6C299]/40 text-pe-accent-text disabled:opacity-50">
                                        Ausdrucken
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default FortbildungModal;
